import React, {useCallback, useContext, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {FlatButton} from '@pb/kit-component-button';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Scrollable} from '@pb/kit-component-scrollable';
import {Pill, StripOfPills} from '@pb/kit-component-strip-of-pills';
import {LocaleContext} from '@pb/kit-context-locale';

import {FILTER_STRIP_LABELS} from '../constants';
import {accountAuditLogStateActions, accountAuditLogStateSelector} from '../slices/account-audit-log-state.slice';
import {AccountAuditLogCriteria} from '../typedef';

import {
    filterStripClearButton,
    filterStripGroup,
    filterStripPill,
} from './filter-strip.module.styl';


type Props = {
    clearAllDisabled: boolean,
    actionTypeDictionary: DictionaryItem[],
    categoryDictionary: DictionaryItem[],
};

export const FilterStrip: React.FC<Props> = ({clearAllDisabled, actionTypeDictionary, categoryDictionary}) => {
    const dispatch = useDispatch();
    const cacheKeys = useSelector(accountAuditLogStateSelector);
    const {locale} = useContext(LocaleContext);

    const onRemovePill = useCallback((propName: keyof AccountAuditLogCriteria, forRemove?: string) => {
        const currentFilters = {...cacheKeys.filters};

        if (Array.isArray(currentFilters[propName])) {
            currentFilters[propName] = (currentFilters[propName] as string[])
                .filter((value: string) => value !== forRemove);
            if (!currentFilters[propName]?.length) {
                delete currentFilters[propName];
            }
        } else {
            delete currentFilters[propName];
        }

        dispatch(accountAuditLogStateActions.setFilters(currentFilters));
    }, [cacheKeys.filters, dispatch]);

    const clearCriteria = useCallback(() => {
        dispatch(accountAuditLogStateActions.setFilters({}));
    }, [dispatch]);

    const getPill = useCallback((labelKey: string, value: string | undefined | Array<string>) => {
        if (labelKey === 'categories') {
            if (!value) {
                return [];
            }
            return (value as string[]).map(category => {
                const categoryItem = categoryDictionary.find(item => item.code === category);
                return {visibleValue: categoryItem?.description || category, value: category};
            });
        }

        if (labelKey === 'actionType') {
            const action = actionTypeDictionary.find(a => a.code === value);
            return {visibleValue: action?.description || value, value};
        }

        if (labelKey === 'startDate' || labelKey === 'endDate') {
            return {
                visibleValue: FormattedDate.format({
                    value: new Date(value as string),
                    format: mapDateFormat(locale.dateFormat, 'NUMERIC'),
                }),
                value,
            };
        }

        return Array.isArray(value)
            ? value.map(v => ({visibleValue: `${v}`, value: v}))
            : {visibleValue: `${value}`, value};
    }, [categoryDictionary, actionTypeDictionary, locale.dateFormat]);

    const stripData = useMemo(() => {
        const {searchQuery, ...otherFilters} = cacheKeys.filters;
        return Object.entries(otherFilters)
            .map(([key, value]) => {
                const labelKey = key as keyof typeof FILTER_STRIP_LABELS;
                const pill = getPill(labelKey, value);
                return {
                    propName: key,
                    pill,
                    label: FILTER_STRIP_LABELS[labelKey] || key,
                };
            })
            .filter(({pill}) => Array.isArray(pill) ? pill.length > 0 : pill && pill.value);
    }, [cacheKeys.filters, getPill]);


    const stripView = useMemo(() => stripData?.map(({propName, pill, label}) => (
        <StripOfPills key={propName} label={label}>
            {Array.isArray(pill) ? pill.map(({visibleValue, value}) => (
                <Pill
                    className={filterStripPill}
                    key={visibleValue}
                    name={visibleValue}
                    removable={true}
                    preventedRemoveHandler={() => onRemovePill(propName as keyof AccountAuditLogCriteria, value)}
                />
            )) : (pill && [
                <Pill
                    className={filterStripPill}
                    key={propName}
                    name={pill.visibleValue}
                    removable={true}
                    preventedRemoveHandler={() => onRemovePill(propName as keyof AccountAuditLogCriteria)}
                />,
            ])}
        </StripOfPills>
    )), [stripData, onRemovePill]);

    return (
        <span className={filterStripGroup}>
            <Scrollable>
                {stripView}
                <FlatButton
                    className={filterStripClearButton}
                    startIcon={<Icon type={Icons.TRASH}/>}
                    onClick={clearCriteria}
                    disabled={clearAllDisabled}>
                    Clear all criteria
                </FlatButton>
            </Scrollable>
        </span>
    );
};
